import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type {
  CustomTagDefinition,
  CustomTagValue,
  PropertyTags,
  ReservationFilter,
} from 'lumen/models';
import type MarketSettingsPropertyTagsOrganizeRoute from 'lumen/routes/market/settings/property-tags/organize';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface CustomTagValuePayload {
  definition_id: number;
  subscription_id: number;
  value: string | number[] | null;
}

export default class MarketSettingsPropertyTagsOrganizeController extends Controller {
  declare model: ModelFrom<MarketSettingsPropertyTagsOrganizeRoute>;

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  saveChangesTask = task(async (): Promise<void> => {
    const marketId = this.hotelAndMarketService.market.id;

    if (!marketId) {
      return;
    }

    try {
      const saves: Promise<unknown>[] = [];

      if (this.dirtyPropertyTags.length > 0) {
        saves.push(this.savePropertyTagsTask.perform(marketId));
      }

      if (this.hasDirtyCustomValues) {
        saves.push(this.saveCustomTagValuesTask.perform(marketId));
      }

      await Promise.all(saves);

      // Unload dirty custom tag value records so the fresh reload starts from a clean slate
      for (const v of [...this.allCustomTagValues]) {
        if (v.isNew || v.isDeleted) {
          v.unloadRecord();
        }
      }

      this.toast.success(this.intl.t('general.changes_saved'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    } catch (error: unknown) {
      this.toast.error(this.intl.t('general.changes_saved.failed'), {
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });

      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    } finally {
      // Always refetch, even on partial failure, so the UI reflects true persisted state
      void this.model.data.perform();
    }
  });

  saveCustomTagValuesTask = task(async (marketId: string): Promise<void> => {
    const dirtyValues = this.allCustomTagValues.filter(
      (v) => (v.isNew || v.isDeleted) && v.definition.id,
    );

    // For each definition, collect the unique subscriptions with dirty values,
    // then build one payload entry per (definition, subscription) pair.
    const payload: CustomTagValuePayload[] = this.definitions.flatMap(
      (definition) => {
        const dirtySubscriptionIds = new Set(
          dirtyValues
            .filter((v) => v.definition.id === definition.id)
            .map((v) => v.subscription.id ?? ''),
        );

        return [...dirtySubscriptionIds].map((subscriptionId) => ({
          subscription_id: Number(subscriptionId),
          definition_id: Number(definition.id),
          value: definition.serializeValuesForPayload(
            this.allCustomTagValues.filter(
              (v) =>
                v.subscription.id === subscriptionId &&
                v.definition.id === definition.id &&
                !v.isDeleted,
            ),
          ),
        }));
      },
    );

    const requestUrl = formatUrl(`/property-tags/custom/`, {
      market_id: marketId,
    });

    await this.ajax.request(requestUrl, {
      namespace: APIGATEWAY,
      type: 'PATCH',
      data: JSON.stringify({ custom_tag_values: payload }),
    });
  });

  savePropertyTagsTask = task(async (marketId: string): Promise<void> => {
    const requestUrl = formatUrl(`/property-tags`, {
      market_id: marketId,
    });

    const requestBody = {
      property_tags: this.dirtyPropertyTags.map((tag) =>
        tag.serialize({ includeId: true }),
      ),
    };

    const propertyTagsResponse = await this.ajax.request<object>(requestUrl, {
      namespace: APIGATEWAY,
      type: 'PATCH',
      data: JSON.stringify(requestBody),
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload('property-tags', propertyTagsResponse);
    this.dirtyPropertyTags.forEach((item) => item.updateRelationships());
  });

  get allCustomTagValues(): CustomTagValue[] {
    return this.propertyTags.flatMap((pt) => pt.subscription.customTagValues);
  }

  get definitions(): CustomTagDefinition[] {
    return this.model.data.last?.value?.definitions ?? [];
  }

  get dirtyPropertyTags(): PropertyTags[] {
    return this.propertyTags.filter((tag) => tag.hasUnsavedChanges);
  }

  get hasDirtyCustomValues(): boolean {
    return this.allCustomTagValues.some((v) => v.isNew || v.isDeleted);
  }

  get hasUnsavedChanges(): boolean {
    return this.dirtyPropertyTags.length > 0 || this.hasDirtyCustomValues;
  }

  get propertyTags(): PropertyTags[] {
    return this.model.data.last?.value?.propertyTags ?? [];
  }

  get reservationFilters(): ReservationFilter[] {
    return this.model.data.last?.value?.reservationFilters ?? [];
  }

  @action
  async cancelChanges(): Promise<void> {
    const isCancelConfirmed = await this.showDiscardChangesAlert();

    if (!isCancelConfirmed) {
      return;
    }

    this.discardChanges();
  }

  @action
  discardChanges(): void {
    this.dirtyPropertyTags.forEach((tag) => {
      tag.rollbackAttributes();
      tag.rollbackRelationships();
    });

    [...this.allCustomTagValues].forEach((v) => {
      if (v.isNew) {
        v.unloadRecord();
      } else {
        v.rollbackAttributes();
      }
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  showDiscardChangesAlert() {
    return this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.property_tags.discard_changes_modal.title'),
      text: this.intl.t(
        'settings.property_tags.discard_changes_modal.description',
      ),
      buttons: {
        cancel: {
          text: this.intl.t(
            'settings.property_tags.discard_changes_modal.cta.cancel',
          ),
        },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });
  }
}
